/**
 * Saved tools and step progress, for signed-in and anonymous visitors alike.
 *
 * The quiz deliberately runs before an account exists, so this has to work in
 * two modes and hand over cleanly between them:
 *
 *   - Signed out, everything lives in localStorage. No network, no rows in the
 *     database for people who bounce.
 *   - Signed in, everything lives in MySQL via tRPC.
 *   - On signup, `useAdoptLocalState` hands the local copy to the server once
 *     and clears it. Someone who ran the quiz and ticked two steps before
 *     making an account keeps both, which is the entire reason for gating
 *     after the quiz rather than in front of it.
 *
 * Local reads and writes go through the same functions in both modes, so
 * components never branch on auth state.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/core/hooks/useAuth";

// Stable empty fallbacks: a fresh [] on each render would defeat the useMemo
// below while the query is still loading or disabled.
const EMPTY_TOOLS: never[] = [];
const EMPTY_STEPS: never[] = [];

const STEP_KEY = "yss:step-progress:v1";
const TOOL_KEY = "yss:saved-tools:v1";

type LocalSteps = Record<string, { toolSlug: string; at: string }>;
type LocalTools = Record<string, { intentId: string | null; at: string }>;

function readJson<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    // Private browsing, quota, or a value someone hand-edited. This is a
    // convenience, never a blocker, so fail to "nothing saved".
    return fallback;
  }
}

function writeJson(key: string, value: unknown) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* see readJson */
  }
}

export const readLocalSteps = () => readJson<LocalSteps>(STEP_KEY, {});
export const readLocalTools = () => readJson<LocalTools>(TOOL_KEY, {});
export function clearLocalState() {
  try {
    window.localStorage.removeItem(STEP_KEY);
    window.localStorage.removeItem(TOOL_KEY);
  } catch {
    /* see readJson */
  }
}

export interface StackApi {
  ready: boolean;
  savedToolIds: Set<string>;
  intentForTool: (toolId: string) => string | null;
  isSaved: (toolId: string) => boolean;
  saveTool: (toolId: string, intentId?: string | null) => void;
  removeTool: (toolId: string) => void;

  completedSteps: Set<string>;
  isStepComplete: (stepId: string) => boolean;
  completedOn: (stepId: string) => string | null;
  setStepComplete: (stepId: string, toolSlug: string, complete: boolean) => void;
  countComplete: (stepIds: string[]) => number;
}

export function useStack(): StackApi {
  const { isAuthenticated, loading } = useAuth();
  const utils = trpc.useUtils();

  const [localSteps, setLocalSteps] = useState<LocalSteps>({});
  const [localTools, setLocalTools] = useState<LocalTools>({});

  // Read after mount. There is no localStorage during a server render, and
  // seeding state from it in the initialiser produces a hydration mismatch.
  useEffect(() => {
    setLocalSteps(readLocalSteps());
    setLocalTools(readLocalTools());
  }, []);

  const remote = trpc.stack.list.useQuery(undefined, {
    enabled: isAuthenticated,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const saveMutation = trpc.stack.save.useMutation({
    onSuccess: () => utils.stack.list.invalidate(),
  });
  const removeMutation = trpc.stack.remove.useMutation({
    onSuccess: () => utils.stack.list.invalidate(),
  });
  const stepMutation = trpc.stack.setStep.useMutation({
    onSuccess: () => utils.stack.list.invalidate(),
  });

  const remoteTools = remote.data?.tools ?? EMPTY_TOOLS;
  const remoteSteps = remote.data?.steps ?? EMPTY_STEPS;

  // Memoised so the sets keep their identity between renders. They are read by
  // the useCallback below, and a fresh Set on every render meant every consumer
  // of setStepComplete re-rendered on every render of this hook.
  const savedToolIds = useMemo(
    () =>
      new Set(
        isAuthenticated ? remoteTools.map(t => t.toolId) : Object.keys(localTools)
      ),
    [isAuthenticated, remoteTools, localTools]
  );
  const completedSteps = useMemo(
    () =>
      new Set(
        isAuthenticated ? remoteSteps.map(s => s.stepId) : Object.keys(localSteps)
      ),
    [isAuthenticated, remoteSteps, localSteps]
  );

  const saveTool = useCallback(
    (toolId: string, intentId: string | null = null) => {
      if (isAuthenticated) {
        saveMutation.mutate({ toolId, intentId });
        return;
      }
      const next = {
        ...readLocalTools(),
        [toolId]: { intentId, at: new Date().toISOString() },
      };
      setLocalTools(next);
      writeJson(TOOL_KEY, next);
    },
    [isAuthenticated, saveMutation]
  );

  const removeTool = useCallback(
    (toolId: string) => {
      if (isAuthenticated) {
        removeMutation.mutate({ toolId });
        return;
      }
      const next = { ...readLocalTools() };
      delete next[toolId];
      setLocalTools(next);
      writeJson(TOOL_KEY, next);
    },
    [isAuthenticated, removeMutation]
  );

  const setStepComplete = useCallback(
    (stepId: string, toolSlug: string, complete: boolean) => {
      // Completing a step puts the tool in your stack if it is not already
      // there.
      //
      // Without this, progress and the stack can disagree: stepProgress rows
      // exist for a tool that savedTools has never heard of, and because My
      // Stack lists saved tools, that work is invisible. Someone finishes two
      // steps on Claude, opens My Stack and sees nothing.
      //
      // A tool you are actively working through is yours by any reasonable
      // reading, so the act of ticking a step is treated as the decision.
      // No intentId: this did not come from a quiz answer.
      if (complete && !savedToolIds.has(toolSlug)) {
        saveTool(toolSlug, null);
      }

      if (isAuthenticated) {
        stepMutation.mutate({ stepId, toolSlug, complete });
        return;
      }
      const next = { ...readLocalSteps() };
      if (complete) next[stepId] = { toolSlug, at: new Date().toISOString() };
      else delete next[stepId];
      setLocalSteps(next);
      writeJson(STEP_KEY, next);
    },
    // savedToolIds is derived each render; saveTool is stable per auth state.
    [isAuthenticated, stepMutation, saveTool, savedToolIds]
  );

  const completedOn = (stepId: string): string | null => {
    const iso = isAuthenticated
      ? remoteSteps.find(s => s.stepId === stepId)?.completedAt
      : localSteps[stepId]?.at;
    if (!iso) return null;
    const date = new Date(iso);
    return Number.isNaN(date.getTime())
      ? null
      : date.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  };

  return {
    // Signed in, wait for the first fetch so the UI does not flash "not
    // saved" on a tool that is saved.
    ready: !loading && (!isAuthenticated || !remote.isPending),
    savedToolIds,
    intentForTool: (toolId: string) =>
      isAuthenticated
        ? (remoteTools.find(t => t.toolId === toolId)?.intentId ?? null)
        : (localTools[toolId]?.intentId ?? null),
    isSaved: (toolId: string) => savedToolIds.has(toolId),
    saveTool,
    removeTool,
    completedSteps,
    isStepComplete: (stepId: string) => completedSteps.has(stepId),
    completedOn,
    setStepComplete,
    countComplete: (stepIds: string[]) =>
      stepIds.filter(id => completedSteps.has(id)).length,
  };
}

/**
 * Hands anonymous state to the server once, immediately after sign-in.
 *
 * Mounted high in the tree. Fires on the transition into an authenticated
 * session, not on every render of an already-signed-in page, and clears the
 * local copy only after the server confirms, so a failed call leaves the data
 * where it was rather than dropping it.
 */
export function useAdoptLocalState() {
  const { isAuthenticated } = useAuth();
  const utils = trpc.useUtils();
  const adopt = trpc.stack.adopt.useMutation();
  const done = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || done.current) return;

    const steps = Object.entries(readLocalSteps()).map(([stepId, v]) => ({
      stepId,
      toolSlug: v.toolSlug,
    }));
    const tools = Object.entries(readLocalTools()).map(([toolId, v]) => ({
      toolId,
      intentId: v.intentId,
    }));
    if (!steps.length && !tools.length) {
      done.current = true;
      return;
    }

    done.current = true;
    adopt
      .mutateAsync({ tools, steps })
      .then(() => {
        clearLocalState();
        utils.stack.list.invalidate();
      })
      .catch(() => {
        // Leave localStorage intact and let the next sign-in try again.
        done.current = false;
      });
  }, [isAuthenticated, adopt, utils]);
}
